import { gsap } from 'gsap';

/**
 * Full Screen Parallax Effect
 * Adds subtle parallax scrolling to full-screen image modules
 */
export default class FullScreenParallax {
    constructor() {
        this.modules = [];
        this.isScrolling = false;
        this.ticking = false;

        this.init();
    }

    init() {
        // Find all full-screen image modules
        const moduleElements = document.querySelectorAll('.module_full-screen-image-with-text');

        if (moduleElements.length === 0) return;

        // Set up each module
        moduleElements.forEach((module, index) => {
            const imageContainer = module.querySelector('.parallax-image-container');
            const image = imageContainer?.querySelector('img');

            if (!imageContainer || !image) return;

            // Store module data
            this.modules.push({
                element: module,
                imageContainer: imageContainer,
                image: image,
                bounds: null
            });

            // Set initial styles for parallax effect
            gsap.set(imageContainer, {
                scale: 1.3, // Higher scale to prevent gaps during unbounded transform
                transformOrigin: 'center center'
            });
        });

        if (this.modules.length > 0) {
            this.bindEvents();
            this.updateBounds();
            this.updateParallax(); // Initial position
        }
    }

    bindEvents() {
        // Throttled scroll event
        window.addEventListener('scroll', () => {
            if (!this.ticking) {
                requestAnimationFrame(() => {
                    this.updateParallax();
                    this.ticking = false;
                });
                this.ticking = true;
            }
        }, { passive: true });

        // Update bounds on resize
        window.addEventListener('resize', () => {
            this.updateBounds();
            this.updateParallax();
        }, { passive: true });
    }

    updateBounds() {
        this.modules.forEach(module => {
            const rect = module.element.getBoundingClientRect();
            module.bounds = {
                top: rect.top + window.pageYOffset,
                bottom: rect.bottom + window.pageYOffset,
                height: rect.height
            };
        });
    }

    updateParallax() {
        const scrollY = window.pageYOffset;
        const windowHeight = window.innerHeight;

        this.modules.forEach(module => {
            if (!module.bounds) return;

            // Check if module is in viewport (with smaller buffer for earlier activation)
            const buffer = windowHeight * 0.2;
            const isInViewport = scrollY + windowHeight + buffer > module.bounds.top &&
                scrollY - buffer < module.bounds.bottom;

            if (!isInViewport) return;

            // Calculate parallax offset
            const moduleCenter = module.bounds.top + (module.bounds.height / 2);
            const viewportCenter = scrollY + (windowHeight / 2);
            const distance = viewportCenter - moduleCenter;

            // Parallax strength (18% of scroll distance, no bounds for smooth movement)
            const parallaxOffset = distance * 0.18;

            // Apply transform with bounds
            gsap.set(module.imageContainer, {
                y: parallaxOffset,
                force3D: true // Hardware acceleration
            });
        });
    }

    /**
     * Clean up event listeners
     */
    destroy() {
        window.removeEventListener('scroll', this.updateParallax);
        window.removeEventListener('resize', this.updateBounds);

        // Reset transforms
        this.modules.forEach(module => {
            gsap.set(module.imageContainer, {
                clearProps: 'all'
            });
        });

        this.modules = [];
    }
}